import React from "react";
import { InputAdornment, TextField } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { parseLotSize } from "../../../services/utilities/lotSize";
import { useLotsField } from "../hooks/useLotsField";
import LotsStepButtons, { lotsStepButtonsSx } from "./LotsStepButtons";

// Lots input for the order window. Lots may have decimals; every edit is written back as a
// whole-number quantity through `onQuantityChange` (the order form's handleQuantityChange), so the
// existing quantity validation, margin and payload flows run exactly as when quantity is typed.
// The sync rules live in useLotsField.
const LotsInput = ({ quantity, lotsize, onQuantityChange }) => {
  const theme = useTheme();
  const lotSize = parseLotSize(lotsize);
  const lots = useLotsField({
    quantity,
    lotSize,
    onQuantityChange: (newQuantity) =>
      onQuantityChange({ target: { value: String(newQuantity) } }),
  });

  return (
    <TextField
      fullWidth
      label="Lots"
      // Text, not number: number inputs report half-typed decimals such as "1." as empty.
      type="text"
      value={lots.value}
      disabled={!lotSize}
      onChange={(e) => lots.handleChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          e.preventDefault();
          lots.handleStep(e.key === "ArrowUp" ? 1 : -1);
        }
      }}
      helperText={lotSize ? `1 lot = ${lotSize} quantity` : "Lot size not available"}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <LotsStepButtons onStep={lots.handleStep} disabled={!lotSize} />
          </InputAdornment>
        ),
        inputProps: {
          inputMode: lots.decimals > 0 ? "decimal" : "numeric",
          autoComplete: "off",
          style: {
            textAlign: "left",
            fontWeight: "bold",
          },
        },
      }}
      sx={{
        ...lotsStepButtonsSx,
        // Hint starts at the field's left edge, same as the GTT modal's helper text.
        "& .MuiFormHelperText-root": { mx: 0.5 },
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: alpha(theme.palette.text.secondary, 0.3),
          },
          "&:hover fieldset": {
            borderColor: theme.palette.primary.main,
          },
        },
      }}
    />
  );
};

export default LotsInput;
